import { useMemo } from 'react';
import type { AssigneeModel, EntityOrSuggestionRef } from '@bliks-io/contracts';
import {
  buildAssigneeLookup,
  lookupAssignee,
  type AssigneeLookup,
  type LookupSourceState
} from '@bliks-io/ui';
import { useOrgMembersForLookup } from '@/api/queries/useOrgMembers';
import { useOrgFunctionsForLookup } from '@/api/queries/useOrgFunctions';
import { useSystemsForLookup } from '@/api/queries/useSystems';
import { useExternalContactsForLookup } from '@/api/queries/useExternalContacts';

/**
 * Build the canonical `AssigneeLookup` — the org's users / orgFunctions / systems
 * / external contacts keyed by id — for resolving the `EntityOrSuggestionRef`
 * slots (`assignedTo`, helper systems, successor orgFunctions/systems,
 * who-else-does-this) the backend ships unpopulated. All four lists are cached,
 * shared React Query queries, so calling this from multiple components is cheap.
 *
 * OrgFunction refs resolve into the unified `orgFunctions` collection regardless of
 * kind (a step may be assigned to a team- or role-kind OrgFunction).
 *
 * Users are fetched without a status filter — the lookup is resolution-only,
 * so suspended (and suspended-then-deleted) assignees must resolve too.
 *
 * All four lists opt into soft-deleted entities (`includeDeleted`) so refs on
 * old recordings still resolve to a name; deleted entries carry `deletedAt`
 * and surfaces render them grayed with a "(deleted)" marker. Pickers must
 * never build their option lists from this lookup.
 */
type LookupSources = Parameters<typeof buildAssigneeLookup>[0];

/**
 * The last lookup built, keyed on the exact arrays it was built from.
 *
 * `useMemo` is per component instance, so every mounted consumer would build its
 * own copy of four org-wide Maps — a transcript with a dozen step views holding
 * a dozen duplicates of the whole org. React Query hands every consumer the SAME
 * array references until a refetch replaces them, so reference equality shares
 * one build safely and stabilises the downstream `useMemo`s. One entry, because
 * the sources change together.
 */
let lastSources: LookupSources | null = null;
let lastLookup: AssigneeLookup | null = null;

function buildAssigneeLookupShared(sources: LookupSources): AssigneeLookup {
  if (
    lastLookup &&
    lastSources &&
    lastSources.users === sources.users &&
    lastSources.orgFunctions === sources.orgFunctions &&
    lastSources.systems === sources.systems &&
    lastSources.externalContacts === sources.externalContacts
  ) {
    return lastLookup;
  }
  lastSources = sources;
  lastLookup = buildAssigneeLookup(sources);
  return lastLookup;
}

/**
 * The stand-in for a list that has not arrived yet. Module-level and frozen,
 * not a `= []` destructuring default: a fresh literal per render churns the
 * `useMemo` dependency below, which both MISSES the shared build cache and
 * evicts its single slot — on every render, for as long as any list is loading.
 */
const NO_ROWS: readonly never[] = Object.freeze([]);

/**
 * Whether to actually fetch the four lists. Defaults to `true`, so a consumer
 * that just wants the lookup says nothing.
 *
 * It exists because the four lists are org-WIDE reads that not every viewer is
 * permitted to make and not every mounted surface needs — a lookup a component
 * merely holds until a dialog opens, or one a viewer without the catalog reads
 * would only collect 403s from. Without it the only gate available is "do not
 * mount the component", which forces the decision one level up and out of the
 * file that knows why it is being made. Disabled, the hook returns empty maps:
 * refs render as raw ids rather than names.
 */
export interface AssigneeLookupOptions {
  enabled?: boolean;
}

export function useAssigneeLookup({ enabled = true }: AssigneeLookupOptions = {}): AssigneeLookup {
  const { data: users = NO_ROWS } = useOrgMembersForLookup({ enabled });
  const { data: orgFunctions = NO_ROWS } = useOrgFunctionsForLookup({ enabled });
  const { data: systems = NO_ROWS } = useSystemsForLookup({ enabled });
  const { data: externalContacts = NO_ROWS } = useExternalContactsForLookup({ enabled });

  // `OrgFunction`/`User`/etc. are directly assignable to the `Populated*`
  // shapes the lookup carries — the compiler owns that guarantee.
  return useMemo<AssigneeLookup>(
    () => buildAssigneeLookupShared({ users, orgFunctions, systems, externalContacts }),
    [users, orgFunctions, systems, externalContacts]
  );
}

/**
 * The lookup and its readiness from ONE set of subscriptions.
 *
 * Calling `useAssigneeLookup()` and `useAssigneeLookupReady()` side by side
 * subscribes to the same four queries twice — React Query dedupes the network
 * request, not the observers — and a transcript holding many embedded views
 * multiplies that per card.
 */
function sourceState(query: {
  isPending: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
}): LookupSourceState {
  if (query.isError) return { status: 'failed', retry: () => void query.refetch() };
  return query.isPending ? { status: 'pending' } : { status: 'ready' };
}

export function useAssigneeLookupWithReadiness({ enabled = true }: AssigneeLookupOptions = {}): {
  lookup: AssigneeLookup;
  /**
   * Every list has settled (arrived or errored) — what a surface built from ALL
   * of them needs. A DISABLED lookup never settles, so a caller that turns the
   * fetch off owns the decision not to wait on this.
   */
  ready: boolean;
  /**
   * Per list, for a surface that reads exactly ONE of them. An entity card
   * resolves in a single map, so waiting on the conjunction would hold its
   * skeleton until the slowest of four org-wide lists returned — three of them
   * for maps it never reads. Keyed by `AssigneeLookup`'s own property names so
   * a caller cannot name a list that does not exist.
   */
  stateBySource: Record<keyof AssigneeLookup, LookupSourceState>;
} {
  const users = useOrgMembersForLookup({ enabled });
  const orgFunctions = useOrgFunctionsForLookup({ enabled });
  const systems = useSystemsForLookup({ enabled });
  const externalContacts = useExternalContactsForLookup({ enabled });

  const lookup = useMemo<AssigneeLookup>(
    () =>
      buildAssigneeLookupShared({
        users: users.data ?? NO_ROWS,
        orgFunctions: orgFunctions.data ?? NO_ROWS,
        systems: systems.data ?? NO_ROWS,
        externalContacts: externalContacts.data ?? NO_ROWS
      }),
    [users.data, orgFunctions.data, systems.data, externalContacts.data]
  );

  const stateBySource = {
    users: sourceState(users),
    orgFunctions: sourceState(orgFunctions),
    systems: sourceState(systems),
    externalContacts: sourceState(externalContacts)
  };

  return {
    lookup,
    // An errored list releases the gate: a surface built from all four then
    // degrades to unresolved refs rather than hanging.
    ready: Object.values(stateBySource).every((state) => state.status !== 'pending'),
    stateBySource
  };
}

/**
 * True once all four lookup lists have settled (data arrived or errored).
 *
 * Surfaces that build their entire layout from the lookup (the editor's
 * swimlanes) flash raw ids / "Unassigned" / "??" chips when rendered against
 * the empty maps a cold cache yields — gate their mount on this instead.
 * Uses `isPending` (v5: no data yet), so an errored list releases the gate
 * and the surface degrades to unresolved refs rather than hanging.
 */
export function useAssigneeLookupReady(): boolean {
  // Same hooks as useAssigneeLookup — React Query dedupes the observers.
  const users = useOrgMembersForLookup();
  const orgFunctions = useOrgFunctionsForLookup();
  const systems = useSystemsForLookup();
  const externalContacts = useExternalContactsForLookup();

  return (
    !users.isPending && !orgFunctions.isPending && !systems.isPending && !externalContacts.isPending
  );
}

/**
 * Resolve a single assignee ref + model into its populated entity (or
 * `undefined`) via the shared lookup. Thin convenience wrapper over the
 * exported `lookupAssignee`.
 */
export function useResolvedAssignee(
  ref: EntityOrSuggestionRef | null | undefined,
  model: AssigneeModel | undefined
) {
  const lookup = useAssigneeLookup();
  return useMemo(() => lookupAssignee(ref, model, lookup), [ref, model, lookup]);
}
